import { PrezemButton } from "components/ui/button";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { NavigateFunction, useNavigate, useParams } from "react-router-dom";
import { useState, useEffect } from "react";
import { 
  FetchOnlinePopupStoreSugarTransactions, 
  FetchOnlinePopupStoreFriendInvitations,
  FetchOnlinePopupStoreDetail 
} from "services/online-popup-stores.service";
import dayjs from "dayjs";

interface SugarTransaction {
  user: {
    uniqueID: string;
    alias: string;
    phoneNumber: string;
  };
  type: string;
  amount: number;
  createdAt: string;
}

interface FriendInvitation {
  id: number;
  created_at: string;
  updated_at: string;
  invitatorNickname: string;
  inviteeNickname: string;
  isGivePoint: boolean;
  onlinePopupStoreID: number;
}

interface SugarTransactionResponse {
  statusCode: number;
  data: SugarTransaction[];
  totalSugar: number;
}

interface FriendInvitationResponse {
  statusCode: number;
  data: FriendInvitation[];
}

const useOnlinePopupStoreStatistics = () => {
  const navigate: NavigateFunction = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [popupStoreDetail, setPopupStoreDetail] = useState<any>(null);
  const [sugarTransactions, setSugarTransactions] = useState<SugarTransaction[]>([]);
  const [totalSugar, setTotalSugar] = useState<number>(0);
  const [friendInvitations, setFriendInvitations] = useState<FriendInvitation[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  const sugarColumns: GridColDef[] = [
    {
      field: 'user.uniqueID',
      headerName: '사용자 ID',
      minWidth: 150,
      valueGetter: (params) => params.row.user?.uniqueID || '',
    },
    {
      field: 'user.alias',
      headerName: '별명',
      minWidth: 150,
      valueGetter: (params) => params.row.user?.alias || '',
    },
    {
      field: 'user.phoneNumber',
      headerName: '전화번호',
      minWidth: 150,
      valueGetter: (params) => params.row.user?.phoneNumber || '',
    },
    {
      field: 'type',
      headerName: '설탕 유형',
      minWidth: 120,
    },
    {
      field: 'amount',
      headerName: '설탕 금액',
      minWidth: 120,
      renderCell: (params) => {
        return `${params.value?.toLocaleString() || 0}원`;
      },
    },
    {
      field: 'createdAt',
      headerName: '지급일',
      minWidth: 180,
      renderCell: (params) => {
        return dayjs(params.value).format('YYYY-MM-DD HH:mm:ss');
      },
    },
  ];

  const invitationColumns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'ID',
      minWidth: 80,
    },
    {
      field: 'invitatorNickname',
      headerName: '초대한 사람',
      minWidth: 150,
    },
    {
      field: 'inviteeNickname',
      headerName: '초대받은 사람',
      minWidth: 150,
    },
    {
      field: 'isGivePoint',
      headerName: '포인트 지급 여부',
      minWidth: 150,
      renderCell: (params) => {
        return params.value ? '지급됨' : '미지급';
      },
    },
    {
      field: 'created_at',
      headerName: '초대일',
      minWidth: 180,
      renderCell: (params) => {
        return dayjs(params.value).format('YYYY-MM-DD HH:mm:ss');
      },
    },
  ];

  const fetchData = async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      const popupStoreId = parseInt(id);

      // 팝업스토어 상세 정보 조회
      const storeDetail = await FetchOnlinePopupStoreDetail(popupStoreId);
      setPopupStoreDetail(storeDetail);

      // 설탕 거래 내역 조회
      const sugarResponse: SugarTransactionResponse = await FetchOnlinePopupStoreSugarTransactions(popupStoreId);
      setSugarTransactions(sugarResponse.data || []);
      setTotalSugar(sugarResponse.totalSugar || 0);

      // 친구 초대 내역 조회
      const invitationResponse: FriendInvitationResponse = await FetchOnlinePopupStoreFriendInvitations(popupStoreId);
      setFriendInvitations(invitationResponse.data || []);
    } catch (error) {
      console.error('통계 데이터 조회 실패:', error);
    } finally {
      setLoading(false);
    }
  };

  const onClickGoBack = () => {
    navigate('/online-popup-stores');
  };

  useEffect(() => {
    fetchData();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    models: { 
      popupStoreDetail,
      sugarTransactions, 
      totalSugar, 
      friendInvitations, 
      sugarColumns, 
      invitationColumns, 
      loading 
    },
    operations: { onClickGoBack, fetchData }
  };
};

export default function OnlinePopupStoreStatistics() {
  const { models, operations } = useOnlinePopupStoreStatistics();

  return (
    <div className="main-content">
      <div className="section-search">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold">
              {models.popupStoreDetail?.name || '온라인 팝업스토어'} 통계
            </h1>
            <p className="text-gray-600 mt-2">
              설탕 거래 내역 및 친구 초대 현황을 확인할 수 있습니다.
            </p>
          </div>
          <PrezemButton variant="outline" onClick={operations.onClickGoBack}>
            ← 목록으로 돌아가기
          </PrezemButton>
        </div>
      </div>

      {/* 통계 요약 */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h3 className="text-lg font-semibold text-gray-800 mb-2">총 설탕 지급액</h3>
          <p className="text-3xl font-bold text-blue-600">
            {models.totalSugar.toLocaleString()}원
          </p>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h3 className="text-lg font-semibold text-gray-800 mb-2">설탕 거래 건수</h3>
          <p className="text-3xl font-bold text-green-600">
            {models.sugarTransactions.length}건
          </p>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h3 className="text-lg font-semibold text-gray-800 mb-2">친구 초대 건수</h3>
          <p className="text-3xl font-bold text-purple-600">
            {models.friendInvitations.length}건
          </p>
        </div>
      </div>

      {/* 설탕 거래 내역 */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold mb-4">설탕 거래 내역</h2>
        <div style={{ height: '400px', width: '100%' }}>
          <DataGrid
            rows={models.sugarTransactions}
            columns={models.sugarColumns}
            loading={models.loading}
            getRowId={(row) => `${row.user.uniqueID}-${row.createdAt}-${row.type}`}
            initialState={{
              pagination: {
                paginationModel: { page: 0, pageSize: 10 },
              },
            }}
            pageSizeOptions={[10, 20, 50]}
          />
        </div>
      </div>

      {/* 친구 초대 현황 */}
      <div>
        <h2 className="text-xl font-semibold mb-4">친구 초대 현황</h2>
        <div style={{ height: '400px', width: '100%' }}>
          <DataGrid
            rows={models.friendInvitations}
            columns={models.invitationColumns}
            loading={models.loading}
            initialState={{
              pagination: {
                paginationModel: { page: 0, pageSize: 10 },
              },
            }}
            pageSizeOptions={[10, 20, 50]}
          />
        </div>
      </div>
    </div>
  );
}
